/**
 * CategoryIcon — bespoke clothing-category glyphs for the customizer rail.
 * lucide-react-native has no hair/hat/scarf/sleeves/shorts/boots equivalents, so
 * these are purpose-drawn paths ported from the design (2px stroke, currentColor).
 */

import React from 'react';
import { Svg, Path, Circle } from 'react-native-svg';
import type { CustomizerCategoryId } from './characterCustomizer.types';

interface CategoryIconProps {
  category: CustomizerCategoryId;
  size?: number;
  color: string;
}

const STROKE = {
  fill: 'none' as const,
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function paths(category: CustomizerCategoryId, color: string): React.ReactNode {
  const s = { ...STROKE, stroke: color };
  switch (category) {
    case 'base':
      return (
        <>
          <Circle cx={12} cy={12} r={9} {...s} />
          <Circle cx={12} cy={10} r={3} {...s} />
          <Path d="M6.5 18.5a6 6 0 0111 0" {...s} />
        </>
      );
    case 'hair':
      return (
        <>
          <Path d="M6 14c0-5 3-9 6-9s6 4 6 9" {...s} />
          <Path d="M9 14V7M12 14V5M15 14V7" {...s} />
        </>
      );
    case 'hat':
      return (
        <>
          <Path d="M3 16c3-1.5 6-2 9-2s6 .5 9 2" {...s} />
          <Path d="M6 15c0-5 2.5-8 6-8s6 3 6 8" {...s} />
          <Circle cx={12} cy={9.5} r={1.6} {...s} />
        </>
      );
    case 'scarf':
      return (
        <>
          <Path d="M7 6c-1 3-1 6 1 8M17 6c1 3 1 6-1 8" {...s} />
          <Path d="M9 14h6l-1 5h-4z" {...s} />
        </>
      );
    case 'sleeves':
      return <Path d="M8 4l4 2 4-2 3 4-3 2v8H8v-8L5 8z" {...s} />;
    case 'pants':
      return <Path d="M7 4h10l-1 16h-3l-1-9-1 9H7z" {...s} />;
    case 'shoes':
      return (
        <>
          <Path d="M3 11l1-4h4l2 3 7 1c2 .3 3 1.5 3 3v2H4a1 1 0 01-1-1z" {...s} />
          <Path d="M4 15h17" {...s} />
        </>
      );
    default:
      return null;
  }
}

export function CategoryIcon({ category, size = 22, color }: CategoryIconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      {paths(category, color)}
    </Svg>
  );
}
